import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { ButtonToolbar } from "react-bootstrap";
import { bindActionCreators } from "redux";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import moment from "moment";

import BasicWithdrawalEmailTemplate from "./BasicWithdrawalEmailTemplate";

import { ArchiveButton, CheckboxButton, EditButton } from "../../../../common";
import { DataTable } from "../../../../common/DataTable";
import BasicCoverageGroupFields from "./BasicCoverageGroupFields";
import BasicWithdrawalConfigurationFields from "./BasicWithdrawalConfigurationFields";

import {
  COVERAGE_GROUPS,
  CLIENT_COVERAGES,
  GROUPED_COVERAGES,
  BILLING_PERIODS,
  WITHDRAWAL_FORM,
  CLIENT_V2
} from "../../../../../utils/data/models";
import * as dataActions from "../../../../../utils/data/dataActions";

class WithdrawalConfigurationDataTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      showDataTable: !!this.props.withdrawalConfiguration.hasWithdrawalForm,
    };

    this.archiveCoverageGroup = this.archiveCoverageGroup.bind(this);
    this.createEmptyCoverageGroupModel = this.createEmptyCoverageGroupModel.bind(
      this
    );
    this.createCoverageGroup = this.createCoverageGroup.bind(this);
    this.getData = this.getData.bind(this);
    this.normalizeInitialData = this.normalizeInitialData.bind(this);
    this.setCellClassName = this.setCellClassName.bind(this);
    this.toggleArchivedCoverageGroups = this.toggleArchivedCoverageGroups.bind(
      this
    );
    this.updateConfiguration = this.updateConfiguration.bind(this);
    this.updateCoverageGroup = this.updateCoverageGroup.bind(this);
    this.updateMessageConfiguration = this.updateMessageConfiguration.bind(
      this
    );
    this.updateRelatedBillingPeriod = this.updateRelatedBillingPeriod.bind(this);
  }
  
  componentWillMount() {
    this.props.dataActions.clearDataList(COVERAGE_GROUPS);
    this.props.dataActions.clearDataList(CLIENT_COVERAGES);
    this.props.dataActions.getDataItem(CLIENT_V2, this.props.clientId);
    this.updateRelatedBillingPeriod();
  }

  componentDidMount() {
    this.updateRelatedBillingPeriod();
  }

  async updateRelatedBillingPeriod(){
    const today = moment.utc().startOf("day").toISOString();
    const billingPeriods = await this.props.dataActions.getDataList(
      BILLING_PERIODS,
      0,
      0,
      "",
      ["startDate:desc"],
      [`clientId==${this.props.clientId}`, `endDate=ge=${today}`]
    );
    this.setState({
      hasRelatedBillingPeriods: hasRelatedBillingPeriod(
        billingPeriods.payload.data
      ),
    });
  }

  archiveCoverageGroup(withdrawalId, isArchived) {
    return this.props.dataActions
      .updateDataItem(COVERAGE_GROUPS, {
        withdrawalId,
        isArchived: !isArchived,
      })
      .then(() => {
        this.getData(
          this.props.offset,
          this.props.limit,
          this.props.q,
          this.props.sortBy,
          this.props.filters
        );
      });
  }

  cleanValues(values) {
    const newValues = { ...values };
    newValues.withdrawalClientCoverages = newValues.withdrawalClientCoverages
      .filter((clientCoverage) => clientCoverage.checked === true)
      .map((clientCoverage) => {
        let data = { ...clientCoverage };
        delete data.checked;
        return data;
      });

    return newValues;
  }

  createCoverageGroup(values) {
    const newValues = this.cleanValues(values);
    return Promise.all([
      this.props.dataActions.createDataItem(COVERAGE_GROUPS, newValues),
    ]);
  }

  createEmptyCoverageGroupModel(clientId) {
    const localizations = {};
    [...this.props.languages]
      .sort((a, b) => a.languageCode.localeCompare(b.languageCode))
      .map((l) => {
        localizations[l.languageCode] = {
          name: "",
          emailMessage: "",
          languageCode: l.languageCode,
        };
      });

    return {
      clientId,
      withdrawalClientCoverages: [],
      localizations,
    };
  }

  generateActionButtons(coverageGroup) {
    return (
      <ButtonToolbar key={coverageGroup.withdrawalId}>
        <EditButton
          title={
            <FormattedMessage id="withdrawalConfiguration.datatable.actions.edit" />
          }
          label={<FormattedMessage id="common.edit" />}
          form={BasicCoverageGroupFields}
          data={
            this.props.active.withdrawalId == coverageGroup.withdrawalId
              ? this.normalizeInitialData(this.props.active)
              : null
          }
          getData={this.props.dataActions.getDataItem.bind(
            this,
            COVERAGE_GROUPS,
            coverageGroup.withdrawalId,
            {
              fields:
                "withdrawalClientCoverages(clientCoverageId,clientCoverage(guarantee(localizations))),*",
            },
            {
              "Accept-Language": this.props.languages.map(
                (l) => l.languageCode
              ),
            }
          )}
          editFunction={this.updateCoverageGroup}
          onModalClosed={this.getData.bind(
            this,
            this.props.offset,
            this.props.limit,
            this.props.q,
            this.props.sortBy,
            this.props.filters
          )}
        />
        <EditButton
          title={
            <FormattedMessage id="withdrawalConfiguration.form.coverageGroupDeactivationEmailTemplate" />
          }
          label={<FormattedMessage id="withdrawalConfiguration.email.view" />}
          form={BasicWithdrawalEmailTemplate}
          data={{
            clientId: this.props.clientId,
            emailMessage:
              coverageGroup.localizations[this.props.authLanguageCode]
                .emailMessage,
          }}
          allowSubmission={false}
        />
        <ArchiveButton
          isArchived={coverageGroup.isArchived}
          archivedName={coverageGroup.name}
          archiveFunction={this.archiveCoverageGroup.bind(
            null,
            coverageGroup.withdrawalId,
            coverageGroup.isArchived
          )}
          label={
            coverageGroup.isArchived ? (
              <FormattedMessage id="common.unarchive" />
            ) : (
              <FormattedMessage id="common.archive" />
            )
          }
        />
      </ButtonToolbar>
    );
  }

  generateAddButton(clientId) {
    return (
      <div>
        <CheckboxButton
          className="include-archived"
          id="archived"
          label={
            <FormattedMessage id="withdrawalConfiguration.datatable.actions.includeArchivedCoverageGroups" />
          }
          checked={!this.props.filters.includes(COVERAGE_GROUPS.archiveFilter)}
          onClick={this.toggleArchivedCoverageGroups}
        />
        <EditButton
          title={
            <FormattedMessage id="withdrawalConfiguration.datatable.actions.add" />
          }
          label={<FormattedMessage id="common.add" />}
          add={true}
          form={BasicCoverageGroupFields}
          data={this.normalizeInitialData(
            this.createEmptyCoverageGroupModel(clientId)
          )}
          editFunction={this.createCoverageGroup}
          onModalClosed={this.getData.bind(
            this,
            this.props.offset,
            this.props.limit,
            this.props.q,
            this.props.sortBy,
            this.props.filters
          )}
        />
      </div>
    );
  }

  getCols() {
    const { authLanguageCode } = this.props;
    return [
      {
        name: (
          <FormattedMessage id="withdrawalConfiguration.datatable.columns.name" />
        ),
        display: (coverageGroup) => (
          <span className={this.setCellClassName(coverageGroup)}>
            {coverageGroup.localizations[authLanguageCode].name}
          </span>
        ),
        widthPct: 20,
      },
      {
        name: (
          <FormattedMessage id="withdrawalConfiguration.datatable.columns.guarantees" />
        ),
        display: (coverageGroup) => (
          <span className={this.setCellClassName(coverageGroup)}>
            {coverageGroup.withdrawalClientCoverages
              .map(
                (groupedCoverage) =>
                  groupedCoverage.clientCoverage.guarantee.localizations[
                    this.props.languageCode
                  ].name
              )
              .join(", ")}
          </span>
        ),
        widthPct: 50,
      },
      {
        name: <FormattedMessage id="datatable.actions" />,
        display: (coverageGroup) => this.generateActionButtons(coverageGroup),
        widthPct: 29.99,
      },
    ];
  }

  getData(offset, limit, q, sortBy, filters) {
    const coverageFilter = [
      `clientCoverages.clientId==${this.props.clientId}`,
      CLIENT_COVERAGES.archiveFilter,
    ];
    return Promise.all([
      this.props.dataActions.getDataList(
        CLIENT_COVERAGES,
        0,
        0,
        "",
        ["guarantee.localizations.name:asc"],
        coverageFilter,
        {},
        {},
        "clientCoverageId,guarantee(localizations)"
      ),
      this.props.dataActions.getPagelessDataList(
        GROUPED_COVERAGES,
        0,
        0,
        "",
        ["withdrawalId:asc"],
        [
          `withdrawals.clientId==${this.props.clientId};withdrawals.isArchived==false`,
        ]
      ),
      this.props.dataActions.getDataList(
        COVERAGE_GROUPS,
        offset,
        limit,
        q,
        sortBy,
        filters,
        {},
        { clientId: this.props.clientId },
        "withdrawalClientCoverages(clientCoverageId,clientCoverage(guarantee(localizations))),*"
      ),
    ]);
  }

  normalizeInitialData(initialData) {
    const data = { ...initialData };
    data.withdrawalClientCoverages = this.setupCoverages(
      data.withdrawalClientCoverages
    );
    return data;
  }

  setCellClassName(coverageGroup) {
    return classNames({
      archived: coverageGroup.isArchived,
    });
  }

  setupCoverages(selectedCoverages) {
    const { clientCoverages } = this.props;

    return clientCoverages.map((clientCoverage) => {
      return {
        ...clientCoverage,
        checked:
          selectedCoverages.findIndex(
            (selectedCoverage) =>
              selectedCoverage.clientCoverageId ==
              clientCoverage.clientCoverageId
          ) >= 0,
      };
    });
  }

  toggleArchivedCoverageGroups() {
    const { archiveFilter } = COVERAGE_GROUPS;
    const isArchivedFiltered = this.props.filters.includes(archiveFilter);
    const filters = isArchivedFiltered
      ? this.props.filters.filter((filter) => filter != archiveFilter)
      : [...this.props.filters, archiveFilter];
    this.getData(
      this.props.offset,
      this.props.limit,
      this.props.q,
      this.props.sortBy,
      filters
    );
  }

  updateConfiguration(event) {
    if (event.target) {
      const field = event.target.name;
      const checked = event.target.checked;
      const clientId = this.props.clientId;
      const model = {path: WITHDRAWAL_FORM.path.replace("{}", clientId)};
      let values = { clientId, [field]: checked };
      if (field === "hasWithdrawalForm") {
        this.setState({ showDataTable: checked });
      }
      this.props.dataActions.simplePost({model, data: values}).then(() => {
        this.props.dataActions.getDataItem(CLIENT_V2, clientId);
      });
    }
  }

  updateMessageConfiguration(formValues) {
    this.saveMessageForm(formValues);
  }

  updateCoverageGroup(values) {
    const newValues = this.cleanValues(values);
    this.props.dataActions.updateDataItem(COVERAGE_GROUPS, newValues);
  }

  saveMessageForm(formValues) {
    const clientId = this.props.clientId;
    const dataToUpdate = { clientId, ...formValues };
    const model = {path: WITHDRAWAL_FORM.path.replace("{}", clientId)};
    this.props.dataActions.simplePost({model, data: dataToUpdate}).then(() => {
      this.props.dataActions.getDataItem(CLIENT_V2, clientId);
    });
  }

  render() {
    return (
      <div>
        {this.state.hasRelatedBillingPeriods !== undefined && (
          <div>
            {
              <BasicWithdrawalConfigurationFields
                ref="form"
                clientId={this.props.clientId}
                initialValues={this.props.withdrawalConfiguration}
                onChange={this.updateConfiguration}
                updateMessageConfiguration={this.updateMessageConfiguration}
                lockMessageForm={this.lockMessageForm}
                unlockMessageForm={this.unlockMessageForm}
                hasRelatedBillingPeriods={this.state.hasRelatedBillingPeriods}
              />
            }
            {this.state.showDataTable && (
              <div>
                <h4>
                  <FormattedMessage id="withdrawalConfiguration.title" />
                </h4>
                <DataTable
                  ref="dataTable"
                  getData={this.getData}
                  cols={this.getCols()}
                  q={this.props.q}
                  filters={this.props.filters}
                  offset={this.props.offset}
                  data={this.props.items}
                  rowsPerPage={this.props.limit}
                  totalRows={this.props.total}
                  sortBy={this.props.sortBy}
                  addButton={this.generateAddButton(this.props.clientId)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

WithdrawalConfigurationDataTable.propTypes = {
  active: PropTypes.object,
  authLanguageCode: PropTypes.string,
  clientCoverages: PropTypes.array,
  clientId: PropTypes.number,
  dataActions: PropTypes.object,
  filters: PropTypes.array,
  groupedCoverages: PropTypes.array,
  items: PropTypes.array,
  languageCode: PropTypes.string,
  languages: PropTypes.array,
  limit: PropTypes.number,
  offset: PropTypes.number,
  q: PropTypes.string,
  sortBy: PropTypes.array,
  total: PropTypes.number,
  withdrawalConfiguration: PropTypes.object,
};

function initializeClientLocalizations(languages, localizations) {
  const result = { ...(localizations || {}) };
  languages
    .sort((a, b) => a.languageCode.localeCompare(b.languageCode))
    .map((language) => {
      const languageCode = language.languageCode;
      result[languageCode] = result[languageCode] || {
        withdrawalFormMessage: "",
      };
    });
  return result;
}

function hasRelatedBillingPeriod(billingPeriods) {
  const today = moment().format("YYYY-MM-DD");
  let relatedPeriods = [];
  (billingPeriods.items || [])
    .filter((item) => item.relatedBillingPeriodId !== null)
    .map((item) => {
      billingPeriods.items
        .filter(
          (i) =>
            i.billingPeriodId == item.relatedBillingPeriodId &&
            i.endWithdrawalDate &&
            i.endWithdrawalDate >= today
        )
        .map((item) => {
          relatedPeriods = [...relatedPeriods, item.billingPeriodId];
        });
    });
  return relatedPeriods.length > 0;
}

function mapStateToProps(state, ownProps) {
  const activeClient = state.data.clients.active;
  const clientCoverages = state.data.clientCoverages || {};
  const data = state.data.coverageGroups || {};
  const groupedCoverages = state.data.groupedCoverages || {};
  const languageCode = state.auth.user.languageCode;
  const pagination = data.pagination || {};

  const {
    clientId,
    hasWithdrawalForm,
    hasWithdrawalRefund,
    mustIncludeWithdrawalCode,
    hasPermanentWithdrawalForm,
    hasAnnualWithdrawalForm,
    hasSessionWithdrawalForm,
    refuteWithdrawalForUnpaidCoverages,
    mustUseInstitutionEmailForWithdrawal,
    hasWithdrawalFormMessage,
    hasWithdrawalPopupMessage,
    hasWithdrawalRedirectButton,
    businessAccountType,
    clientBillingPeriodType,
  } = activeClient;

  const filters =
    data.clientId != clientId
      ? [
          `withdrawals.clientId==${clientId}`,
          COVERAGE_GROUPS.archiveFilter,
          CLIENT_COVERAGES.archiveFilter,
        ]
      : data.filters;

  const localizations = initializeClientLocalizations(
    ownProps.languages,
    activeClient.localizations
  );
  const withdrawalConfiguration = {
    hasWithdrawalForm,
    hasWithdrawalRefund,
    mustIncludeWithdrawalCode,
    hasPermanentWithdrawalForm,
    hasAnnualWithdrawalForm,
    hasSessionWithdrawalForm,
    refuteWithdrawalForUnpaidCoverages,
    mustUseInstitutionEmailForWithdrawal,
    hasWithdrawalFormMessage,
    hasWithdrawalPopupMessage,
    hasWithdrawalRedirectButton,
    localizations,
    businessAccountType,
    clientBillingPeriodType,
  };

  return {
    active: data.active || {},
    authLanguageCode: state.auth.user.languageCode,
    clientCoverages: (clientCoverages.items || []).map((item) => {
      let data = { ...item };
      const name = item.guarantee.localizations[languageCode]
        ? item.guarantee.localizations[languageCode].name
        : "";
      delete data.guarantee;
      return { ...data, name };
    }),
    clientId: clientId,
    filters,
    groupedCoverages: groupedCoverages.items || [],
    items: data.items || null,
    languageCode,
    limit: pagination.limit || 100,
    offset: pagination.offset || 0,
    sortBy: data.sortBy || ["localizations.name:desc"],
    total: pagination.totalCount || 0,
    withdrawalConfiguration,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(WithdrawalConfigurationDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/withdrawal-configuration/WithdrawalConfigurationDataTable.js